import { useState, useRef } from 'react';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

export default function ReceiptScanner({ onAdd, onClose }) {
  const [preview, setPreview]     = useState(null);   // data URL for img preview
  const [file, setFile]           = useState(null);
  const [scanning, setScanning]   = useState(false);
  const [scanned, setScanned]     = useState(null);   // [{ name, quantity, unit, selected }]
  const [error, setError]         = useState('');
  const fileRef                   = useRef();

  function handleFile(f) {
    if (!f) return;
    setFile(f);
    setScanned(null);
    setError('');
    const reader = new FileReader();
    reader.onload = e => setPreview(e.target.result);
    reader.readAsDataURL(f);
  }

  async function scan() {
    if (!file) return;
    setScanning(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('image', file);
      const res  = await fetch(`${API}/scan/receipt`, { method: 'POST', body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Scan failed');
      if (!data.items?.length) throw new Error('No items found on receipt. Try a clearer photo.');
      setScanned(data.items.map(i => ({ ...i, selected: true })));
    } catch (e) {
      setError(e.message);
    } finally {
      setScanning(false);
    }
  }

  function toggleItem(idx) {
    setScanned(prev => prev.map((it, i) => i === idx ? { ...it, selected: !it.selected } : it));
  }

  function addSelected() {
    const chosen = scanned.filter(i => i.selected).map(i => ({
      id:         crypto.randomUUID(),
      name:       i.name,
      quantity:   i.quantity,
      unit:       i.unit,
      is_checked: false,
      added_via:  'receipt',
    }));
    if (chosen.length) onAdd(chosen);
    onClose();
  }

  const selectedCount = scanned ? scanned.filter(i => i.selected).length : 0;

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="scan-modal">
        <div className="scan-modal-header">
          <span>🧾 Scan Receipt</span>
          <button className="scan-modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="scan-modal-body">
          {!scanned ? (
            <>
              <div
                className="scan-dropzone"
                onClick={() => fileRef.current?.click()}
              >
                {preview ? (
                  <img src={preview} alt="Receipt" className="scan-preview-img" />
                ) : (
                  <>
                    <span className="scan-dropzone-icon">🧾</span>
                    <p>Tap to upload or take a photo of your receipt</p>
                    <p className="scan-dropzone-sub">JPG, PNG, HEIC supported</p>
                  </>
                )}
              </div>

              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                capture="environment"
                style={{ display: 'none' }}
                onChange={e => handleFile(e.target.files?.[0])}
              />

              {error && <p className="scan-error">{error}</p>}

              <div className="scan-modal-actions">
                <button className="scan-btn-cancel" onClick={onClose}>Cancel</button>
                <button
                  className="scan-btn-primary"
                  onClick={scan}
                  disabled={!file || scanning}
                >
                  {scanning ? (
                    <><span className="spinner-sm" /> Scanning…</>
                  ) : 'Scan Receipt'}
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="scan-found-msg">
                Found {scanned.length} item{scanned.length !== 1 ? 's' : ''} — uncheck any you don't want
              </p>
              <ul className="scan-items-list">
                {scanned.map((item, idx) => (
                  <li key={idx} className="scan-item-row">
                    <button
                      className={`sli-checkbox ${item.selected ? 'sli-checkbox--checked' : ''}`}
                      onClick={() => toggleItem(idx)}
                    >
                      {item.selected && (
                        <svg viewBox="0 0 12 10" width="12" height="10" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="1,5 4.5,8.5 11,1" />
                        </svg>
                      )}
                    </button>
                    <span className="scan-item-name">{item.name}</span>
                    <span className="scan-item-qty">
                      {item.quantity !== 1 ? `×${item.quantity}` : ''}
                      {item.unit ? ` ${item.unit}` : ''}
                    </span>
                  </li>
                ))}
              </ul>

              {error && <p className="scan-error">{error}</p>}

              <div className="scan-modal-actions">
                <button className="scan-btn-cancel" onClick={() => setScanned(null)}>← Rescan</button>
                <button
                  className="scan-btn-primary"
                  onClick={addSelected}
                  disabled={selectedCount === 0}
                >
                  Add {selectedCount} item{selectedCount !== 1 ? 's' : ''}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
